import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import ChevronDown from '../../images/ico_chevron_down.svg'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClose } from '@fortawesome/free-solid-svg-icons'
import { useController, Control } from 'react-hook-form'

interface SelectProps {
  name: string
  options: Options[]
  placeHolderText: string
  icon?: string
  dataFromSelect?: any
  isTextBold?: boolean
  required?: boolean
  selectContainerBackground?: string
  control: Control<any>
  rules?: any
}

interface OptionLiProps {
    isHighlighted?: boolean;
}

export interface Options {
  value: string
  label: string
}

export interface ISelectData {
  name: string
  value: string
  category: string
}

const Select: React.FC<SelectProps> = ({ name, options, placeHolderText, icon, dataFromSelect, isTextBold = false, required = false, selectContainerBackground, control, rules }) => {
  const [areOptionsOpen, setAreOptionsOpen] = useState<boolean>(false)
    const { field, fieldState } = useController({name, control, rules})

  const dataFromLocalStorage: ISelectData = JSON.parse(localStorage.getItem('selectData') || '[]').find(
    (o: any) => o.category === name
  )

  const [selectedData, setSelectedData] = useState(
    dataFromLocalStorage || {
      name: '',
      value: '',
      category: ''
    }
  )

  const ref = useRef<any>(null)
    const highlightedRef = useRef<any>(null)
  // setta i campi dalla select quando si clicca su una delle opzioni
  const handleSelectChange = (opt: Options) => {
    const newValue = { name: opt.label, value: opt.value, category: name }
    field.onChange(opt.value)
    dataFromSelect && dataFromSelect(newValue)

    setSelectedData(newValue)

    let localStorageData = JSON.parse(localStorage.getItem('selectData') || '[]')
    const previousFieldIndex = localStorageData.findIndex((elem: any) => elem.category === name)
    if(previousFieldIndex > -1) {
      localStorageData[previousFieldIndex] = newValue
      localStorage.setItem('selectData', JSON.stringify(localStorageData))
    } else {
      localStorageData = [...localStorageData, newValue]
      localStorage.setItem('selectData', JSON.stringify(localStorageData))
    }

    setAreOptionsOpen(false)
  }

    // elimina i campi dalla select quando si clicca sulla x
    const handleDeleteField: React.EventHandler<React.SyntheticEvent> = (e) => {
        e.stopPropagation()
        dataFromSelect && dataFromSelect({ name: '', value: '', category: selectedData.category })
        setSelectedData({ name: '', value: '', category: selectedData.category })
    }
    const handleDeleteFieldWithKeyboard: React.EventHandler<React.KeyboardEvent> = (e) => {
        if(e.key === 'Enter' || e.key === ' '){
            e.preventDefault()
            handleDeleteField(e)
        }
    }

  // gestisce l'apertura della select con la navigazione da tastiera
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const currentIndex = options.findIndex(o => o.value === selectedData.value)

        switch (e.key) {
            case 'Tab':
                if (areOptionsOpen) {
                    e.preventDefault();
                    setAreOptionsOpen(false)
                }
                return

            case 'ArrowDown': {
                e.preventDefault()
                if (!areOptionsOpen) setAreOptionsOpen(true)
                const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % options.length
                setSelectedData({ ...selectedData, ...options[nextIndex], category: name })
                break
            }
            case 'ArrowUp': {
                e.preventDefault()
                if (!areOptionsOpen) break
                const previousIndex = (currentIndex - 1 + options.length) % options.length
                setSelectedData({ ...selectedData, ...options[previousIndex], category: name })
                break
            }
            case 'Enter':
            case ' ':
                e.preventDefault()
                if (areOptionsOpen) {
                    const validIndex = currentIndex === -1 ? 0 : currentIndex
                    handleSelectChange(options[validIndex])
                } else {
                    setAreOptionsOpen(true)
                }
                break
            case 'Escape':
                e.preventDefault()
                setAreOptionsOpen(false)
                break
        }
    }


  // gestisce l'apertura e la chiusura del menu a tendina con il mouse
    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.detail === 0) return
        setAreOptionsOpen(!areOptionsOpen)
    }


  // gestisce l'apertura e la chiusura del menu a tendina
  const handleClickOutside = (event: MouseEvent) => {
    if (ref.current && areOptionsOpen && !ref.current.contains(event.target)) {
        setAreOptionsOpen(false)
    }
  }

  useLayoutEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [handleClickOutside])

    useLayoutEffect(() => {
        highlightedRef.current?.scrollIntoView({block: "nearest"});
    }, [selectedData])

  // Richiamo dataFromSelect() per impostare il primo valore nel caso in cui l'utente non abbia ancora interagito con la select
  useEffect(() => {
    dataFromSelect(selectedData)
  }, [])

  return (
    <div style={{ width: '100%' }}>
      <SelectContainer
          tabIndex={0}
          ref={ref}
          onKeyDown={handleKeyDown}
          onClick={handleClick}
          open={areOptionsOpen}
          backgroundColor={selectContainerBackground}
          aria-label={name}
      >
        {icon ? (
          <div
            style={{
              backgroundImage: `url(${icon})`,
              backgroundAttachment: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              height: '100%',
              width: '80px',
              marginLeft: 2
            }}
          />
        ) : <Filler/>}
        <SelectedValue isTextBold={isTextBold}>
          <SelectedValueLabel>{selectedData.name !== '' ? selectedData.name : placeHolderText}</SelectedValueLabel>
          {selectedData.name && (
            <FontAwesomeIcon
                tabIndex={0}
                role={"button"}
                aria-label="Cancella selezione"
              onClick={handleDeleteField}
              onKeyDown={handleDeleteFieldWithKeyboard}
              icon={faClose}
              style={{
                placeSelf: 'center',
                fontWeight: '200',
                fontSize: '15px',
                color: '#000',
                cursor: 'pointer'
              }}
            />
          )}
        </SelectedValue>
        <div
          style={{
            backgroundImage: `url(${ChevronDown})`,
            backgroundAttachment: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            cursor: 'pointer',
            width: '60px',
            height: '100%',
            transform: areOptionsOpen ? 'rotate(180deg)' : '',
            transition: 'transform 0.2s'
          }}
        />
        <OptionsWrapper
            onClick={(e) => e.stopPropagation()}>
          {areOptionsOpen && (
            <DropDownList role="listbox">
                {options.map((opt, index) => {
                    return (
                        <OptionLi
                            role='option'
                            key={index}
                            ref={(e) => {
                                if(opt.value === selectedData.value) {
                                    highlightedRef.current = e;
                                }
                            } }
                            isHighlighted={opt.value === selectedData.value}
                            onClick={() => handleSelectChange(opt)}
                            onKeyDown={() => handleSelectChange(opt)}
                        >
                            {opt.label}&nbsp;
                        </OptionLi>
                    )
                })}

            </DropDownList>
          )}
        </OptionsWrapper>
      </SelectContainer>
      {fieldState.error && (
         <ErrorText>{fieldState.error.message}</ErrorText>
      )}
    </div>
  )
}

export default Select

// region Style
const Filler = styled.div`
  width: 20px;
`
const SelectContainer = styled.div<{ open: boolean, backgroundColor?: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${({backgroundColor}) => (backgroundColor ? backgroundColor : '#fff')};
  height: 50px;
  padding: 0;
  margin: 0 5px;
  width: 100%;
  font-family: TradeGothicLTPro, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
  color: #222;
  font-size: 1.3rem;
  border-radius: ${(props) => (props.open ? '30px 30px 0 0' : '30px')};
  transition: border-radius 0.3s;
  position: relative;
  cursor: pointer;
`
const OptionsWrapper = styled.div`
  z-index: 2;
  position: absolute;
  top: 50px;
  left: 0;
  width: 100%;
  border: none;
  overflow: hidden;
  box-shadow: 0 3px 18px 0 rgb(45 46 46 / 15%);
  background: #ffffff;
  box-sizing: border-box;
  border-radius: 0 0 30px 30px;
`
const DropDownList = styled.ul`
  list-style: none;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  background: #ffffff;
  box-sizing: border-box;
  color: #222;
  border-radius: 0 0 30px 30px;
  margin: 0;
  padding: 0;
  max-height: 240px;

  & > li {
    cursor: pointer;
    text-align: left;
  }
  & > li:last-child {
    border-radius: 0 0 30px 30px;
  }
`
const SelectedValue = styled.div<any>`
  text-align: initial;
  display: grid;
  grid-template-columns: 1fr auto;
  place-content: center;
  width: 100%;
  letter-spacing: -0.5px;
  ${props => props.isTextBold ? 'font-weight: bold' : ''};
`
const SelectedValueLabel = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  font-size: 1.3rem;
`
const OptionLi = styled.li`
  display: list-item;
  cursor: pointer;
  padding: 0.8rem 1.5rem;
  list-style: none;
  line-height: 1;
  font-size: 1.3rem;
  font-weight: 400;
  width: 100%;
    background-color: ${({ isHighlighted } : OptionLiProps) => isHighlighted ? '#fcbb00' : '#ffffff'};
    color: ${({ isHighlighted } : OptionLiProps) => isHighlighted ? '#000000' : 'inherit'};
    &:hover {
    background-color: #fcbb00;
    color: #000000;
  }
`

const ErrorText = styled.div`
  color: black;
    font-weight: bold ;
    background-color: #FCBB00;
    border-radius: 0.7rem;
    padding: .4rem;
    line-height: 1.3rem;
    border: 1px solid black;
    font-size: 1.3rem;
    margin-top: .3rem;
`
// endregion
